import React, { useState } from "react";
import cx from "classnames";
import { CloseIcon, CloseIconBig, CloseIconSmall } from "../../../assets/icons";
import { Button } from "../../common";

interface ProtectedExpenseModalProps {
  setIsOpenModal: (open: boolean) => void;
  handleProtectedChange: () => Promise<void> | void; // Ensure this can handle async operations
  isProtected: boolean;
}

const ProtectedExpenseModal = ({
  handleProtectedChange,
  setIsOpenModal,
  isProtected,
}: ProtectedExpenseModalProps) => {
  const [loading, setLoading] = useState(false); // Loading state

  const handleClick = async () => {
    setLoading(true); // Set loading to true when the action starts
    try {
      await handleProtectedChange(); // Await the async operation
    } catch (error) {
      console.error("Error updating protected status:", error);
    } finally {
      setLoading(false); // Reset loading state
      setIsOpenModal(false); // Close the modal after the operation
    }
  };

  return (
    <>
      {/* Desktop Modal */}
      <div className="bg-gray-900 bg-opacity-50 fixed top-0 right-0 left-0 z-50 h-[100vh]">
        <div className="hidden md:flex justify-center items-center h-full w-full">
          <div
            className={`overflow-y-auto overflow-x-hidden fixed md:top-[25%] w-full z-50 md:w-[440px] lg:w-[500px] px-4 md:px-6 pb-8 pt-4 bg-white rounded-t-[8px] md:rounded-[8px]`}
          >
            <div className="flex justify-between items-center w-full">
              <p className="text-[18px] font-semibold leading-[30px] text-[#000000]">
                {isProtected ? "Unprotect Expense" : "Protected Expense"}
              </p>
              <div
                className="cursor-pointer flex justify-center items-center w-11 h-11 pr-[1em]"
                onClick={() => setIsOpenModal(false)}
              >
                <CloseIcon />
              </div>
            </div>
            <div className="mt-">
              <p className="text-[16px] leading-[22.4px] mt-[8px] text-gray-600 mb-[28px]">
                {isProtected
                  ? "Are you sure you want to mark this expense as unprotected?"
                  : "Marking your expense as protected ensures that only admins can view this expense. Are you sure you want to mark this expense as protected?"}
              </p>
            </div>

            <div className="flex items-center w-full gap-6">
              <div className="ml-auto w-[330px] flex gap-6">
                <Button
                  onClick={() => setIsOpenModal(false)}
                  outline
                  variant="secondary"
                  text="Cancel"
                  font="semibold"
                />
                <Button
                  onClick={handleClick} // Use the new handler
                  variant="secondary"
                  text={
                    loading
                      ? "Loading..."
                      : isProtected
                      ? "Yes, Continue"
                      : "Mark as protected"
                  }
                  font="semibold"
                  disabled={loading} // Disable the button when loading
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Modal */}
      <div
        className={cx({
          "md:hidden fixed top-0 right-0 bottom-0 left-0 z-50 overflow-y-auto overflow-x-hidden items-start flex justify-end bg-gray-900 bg-opacity-50 h-full":
            true,
          "blur-background": true,
        })}
      >
        <div className="md:hidden h-[300px] rounded-t-[12px] bg-white z-10 w-full absolute bottom-0 p-6">
          <div className="flex justify-between items-center">
            <p className="text-[18px] 360:text-[18px] font-semibold leading-[30px] text-[#000000]">
              {isProtected ? "Unprotect Expense" : "Protected Expense"}
            </p>
            <div onClick={() => setIsOpenModal(false)}>
              <CloseIcon color="#1F2937" />
            </div>
          </div>
          <p className="text-[14px] leading-[19.6px] font-medium mt-[12px] text-gray-600 mb-[43px]">
            {isProtected
              ? "Are you sure you want to mark this expense as unprotected?"
              : "Marking your expense as protected ensures that only admins can view this expense. Are you sure you want to mark this expense as protected?"}
          </p>
          <div className="flex justify-center">
            <div className="w-[302px] space-y-3">
              <Button
                onClick={handleClick} // Use the new handler
                variant="secondary"
                text={
                  loading
                    ? "Loading..."
                    : isProtected
                    ? "Yes, Continue"
                    : "Mark as protected"
                }
                size="full"
                disabled={loading} // Disable the button when loading
              />
              <Button
                onClick={() => setIsOpenModal(false)}
                outline
                variant="secondary"
                text="Cancel"
                size="full"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProtectedExpenseModal;